import { isPlatformBrowser } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, NgZone, PLATFORM_ID, Injectable, EventEmitter, ElementRef, Directive, Input, Output, ChangeDetectorRef, HostBinding, NgModule } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * A service wrapping `ResizeObserver`. This is a service for those reasons:
 * - only one `ResizeObserver` should be used for performance reason.
 * - For Angular change detection to work, explicity `ngZone` calls are necessary
 * - Observable stream
 */
class ResizeObserverService {
    listeners = new Map();
    resizeObserver;
    timerQueue = new Map();
    zone = inject(NgZone);
    constructor() {
        const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        if (!isBrowser || !ResizeObserver) {
            return;
        }
        this.resizeObserver = new ResizeObserver((entries) => entries.forEach(entry => this.handleElement(entry.target)));
    }
    /**
     * Observe the size of an element. Returns an observable with the changes.
     * @param element The element to observe
     * @param throttle Throttle time in ms. Will emit this time after the resize
     * @param emitInitial Emit the initial size after subscribe?
     * @param emitImmediate Emit an event immediately after the size changes. Useful e.g. for visibility checks.
     */
    observe(element, throttle, emitInitial, emitImmediate) {
        let entry = this.listeners.get(element);
        if (!entry) {
            entry = { element, subscribers: [] };
            this.listeners.set(element, entry);
        }
        return new Observable(subscriber => {
            const sub = {
                sub: subscriber,
                dim: undefined,
                throttle,
                blocked: false,
                emitImmediate
            };
            this.subscriberAdded(entry, sub, emitInitial);
            return () => this.subscriberRemoved(entry, sub);
        });
    }
    subscriberAdded(entry, subscriber, emitInitial) {
        entry.subscribers.push(subscriber);
        if (entry.subscribers.length === 1) {
            this.resizeObserver?.observe(entry.element);
        }
        if (emitInitial) {
            this.schedule(0, entry.element, subscriber, false, true);
        }
    }
    subscriberRemoved(entry, subscriber) {
        const index = entry.subscribers.indexOf(subscriber);
        if (index >= 0) {
            entry.subscribers.splice(index, 1);
        }
        if (entry.subscribers.length === 0) {
            // no more subscribers, tear down everything
            this.resizeObserver?.unobserve(entry.element);
            this.listeners.delete(entry.element);
        }
        this.unschedule(subscriber);
        // close down, no re-subscription possible
        subscriber.sub.complete();
    }
    handleElement(element) {
        const entry = this.listeners.get(element);
        if (!entry) {
            this.resizeObserver?.unobserve(element);
            return;
        }
        entry.subscribers.forEach(sub => this.handleResizeSubscriber(element, sub));
    }
    handleResizeSubscriber(element, entry) {
        if (entry.blocked) {
            return;
        }
        if (entry.emitImmediate) {
            this.schedule(0, element, entry, false);
        }
        this.schedule(entry.throttle, element, entry, true);
    }
    emitSize(element, entry, force = false) {
        const dimensions = { width: element.clientWidth, height: element.clientHeight };
        if (!force &&
            entry.dim?.width === dimensions.width &&
            entry.dim?.height === dimensions.height) {
            // Prevent spurious emissions. Subpixels and all..
            return;
        }
        entry.dim = dimensions;
        entry.sub.next(dimensions);
    }
    schedule(timeout, element, subscriber, unblock, force = false) {
        if (unblock) {
            subscriber.blocked = true;
        }
        let queue = this.timerQueue.get(timeout);
        if (!queue) {
            queue = [];
            this.timerQueue.set(timeout, queue);
            setTimeout(() => {
                this.timerQueue.delete(timeout);
                this.processQueue(queue);
            }, timeout);
        }
        queue.push({ element, subscriber, unblock, force });
    }
    unschedule(entry) {
        const queued = this.timerQueue.get(entry.throttle);
        if (queued) {
            const index = queued.findIndex(q => q.subscriber === entry);
            if (index > -1) {
                queued.splice(index, 1);
            }
        }
    }
    processQueue(queue) {
        this.zone.run(() => {
            queue?.forEach(q => {
                if (q.unblock) {
                    q.subscriber.blocked = false;
                }
                this.emitSize(q.element, q.subscriber, q.force);
            });
        });
    }
    /**
     * check size on all observed elements. Only use in testing!
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _checkAll() {
        this.listeners.forEach(entry => this.handleElement(entry.element));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: ResizeObserverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: ResizeObserverService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: ResizeObserverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Directive to emit events on element size change. Use like this:
 *   `<div (siResizeObserver)="handleResize($event)">`
 * When the size of the element changes, an event in the format
 *   `{ width: number, height: number }`
 * will be emitted. Also an initial event will be emitted on init.
 *
 * By default, events are throttled and to an event every 100ms. To change
 * this, add `[resizeThrottle]="200"` on the same element. Input in milliseconds.
 */
class SiResizeObserverDirective {
    resizeThrottle = 100;
    siResizeObserver = new EventEmitter();
    subs;
    element = inject(ElementRef);
    service = inject(ResizeObserverService);
    ngOnInit() {
        this.subs = this.service
            .observe(this.element.nativeElement, this.resizeThrottle, true)
            .subscribe(this.siResizeObserver);
    }
    ngOnDestroy() {
        this.subs?.unsubscribe();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiResizeObserverDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiResizeObserverDirective, selector: "[siResizeObserver]", inputs: { resizeThrottle: "resizeThrottle" }, outputs: { siResizeObserver: "siResizeObserver" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiResizeObserverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siResizeObserver]'
                }]
        }], propDecorators: { resizeThrottle: [{
                type: Input
            }], siResizeObserver: [{
                type: Output
            }] } });

// keep in sync with the Bootstrap variables
const BOOTSTRAP_BREAKPOINTS = {
    smMinimum: 576,
    mdMinimum: 768,
    lgMinimum: 992,
    xlMinimum: 1200,
    xxlMinimum: 1400
};
/**
 * Directive to automatically set `si-container-*` classes so Bootstrap column classes work
 * in the context of the container instead of viewport size.
 */
class SiResponsiveContainerDirective {
    isXs = false;
    isSm = false;
    isMd = false;
    isLg = false;
    isXl = false;
    isXxl = false;
    resizeThrottle = 100;
    breakpoints;
    subs;
    element = inject(ElementRef);
    service = inject(ResizeObserverService);
    changeDetectorRef = inject(ChangeDetectorRef);
    ngOnInit() {
        this.subs = this.service
            .observe(this.element.nativeElement, this.resizeThrottle, true)
            .subscribe(event => this.setResponsiveSize(event.width, event.height));
    }
    ngOnDestroy() {
        this.subs?.unsubscribe();
    }
    setResponsiveSize(width, height) {
        if (!width && !height) {
            // element is not visible, no point in changing anything
            return;
        }
        const breakpoints = this.breakpoints ?? BOOTSTRAP_BREAKPOINTS;
        const oldXs = this.isXs;
        const oldSm = this.isSm;
        const oldMd = this.isMd;
        const oldLg = this.isLg;
        const oldXl = this.isXl;
        const oldXxl = this.isXxl;
        this.isXs = width < breakpoints.smMinimum;
        this.isSm = width >= breakpoints.smMinimum && width < breakpoints.mdMinimum;
        this.isMd = width >= breakpoints.mdMinimum && width < breakpoints.lgMinimum;
        this.isLg = width >= breakpoints.lgMinimum && width < breakpoints.xlMinimum;
        this.isXl = width >= breakpoints.xlMinimum && width < breakpoints.xxlMinimum;
        this.isXxl = width >= breakpoints.xxlMinimum;
        if (oldXs !== this.isXs ||
            oldSm !== this.isSm ||
            oldMd !== this.isMd ||
            oldLg !== this.isLg ||
            oldXl !== this.isXl ||
            oldXxl !== this.isXxl) {
            this.changeDetectorRef.markForCheck();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiResponsiveContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiResponsiveContainerDirective, isStandalone: true, selector: "[siResponsiveContainer]", inputs: { resizeThrottle: "resizeThrottle", breakpoints: "breakpoints" }, host: { properties: { "class.si-container-xs": "this.isXs", "class.si-container-sm": "this.isSm", "class.si-container-md": "this.isMd", "class.si-container-lg": "this.isLg", "class.si-container-xl": "this.isXl", "class.si-container-xxl": "this.isXxl" } }, exportAs: ["siResponsiveContainer"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiResponsiveContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siResponsiveContainer]',
                    exportAs: 'siResponsiveContainer',
                    standalone: true
                }]
        }], propDecorators: { isXs: [{
                type: HostBinding,
                args: ['class.si-container-xs']
            }], isSm: [{
                type: HostBinding,
                args: ['class.si-container-sm']
            }], isMd: [{
                type: HostBinding,
                args: ['class.si-container-md']
            }], isLg: [{
                type: HostBinding,
                args: ['class.si-container-lg']
            }], isXl: [{
                type: HostBinding,
                args: ['class.si-container-xl']
            }], isXxl: [{
                type: HostBinding,
                args: ['class.si-container-xxl']
            }], resizeThrottle: [{
                type: Input
            }], breakpoints: [{
                type: Input
            }] } });

class SiResizeObserverModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiResizeObserverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiResizeObserverModule, declarations: [SiResizeObserverDirective], imports: [SiResponsiveContainerDirective], exports: [SiResizeObserverDirective, SiResponsiveContainerDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiResizeObserverModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiResizeObserverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiResizeObserverDirective],
                    imports: [SiResponsiveContainerDirective],
                    exports: [SiResizeObserverDirective, SiResponsiveContainerDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BOOTSTRAP_BREAKPOINTS, ResizeObserverService, SiResizeObserverDirective, SiResizeObserverModule, SiResponsiveContainerDirective };
